import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ClipboardCheck, Check, X, Loader2, Save, GraduationCap, ChefHat, FileText, Search, Eye, Zap } from 'lucide-react';

const AttendancePage = () => {
    const { getAuthHeaders, API } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]); // combined lessons + assessments + skill assessments
    const [students, setStudents] = useState([]);
    const [attendance, setAttendance] = useState({});
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedItemType, setSelectedItemType] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [groups, setGroups] = useState([]);
    const [selectedGroupFilter, setSelectedGroupFilter] = useState('all');
    const [selectedYear, setSelectedYear] = useState('all');
    const [itemSearch, setItemSearch] = useState('');

    useEffect(() => {
        fetchItems();
    }, []);

    useEffect(() => {
        if (selectedItem) {
            fetchStudentsAndAttendance();
        }
    }, [selectedItem]);

    const fetchItems = async () => {
        try {
            const [lessonsRes, assessmentsRes, skillRes] = await Promise.all([
                axios.get(`${API}/lessons`, { headers: getAuthHeaders() }),
                axios.get(`${API}/assessments`, { headers: getAuthHeaders() }),
                axios.get(`${API}/skill-assessments`, { headers: getAuthHeaders() })
            ]);
            const lessons = lessonsRes.data.map(l => ({ ...l, _type: 'lesson' }));
            const assessments = assessmentsRes.data.map(a => ({ ...a, _type: 'assessment' }));
            const skillAssessments = skillRes.data.map(s => ({ ...s, _type: 'skill_assessment' }));
            setItems([...lessons, ...assessments, ...skillAssessments].sort((a, b) => (b.date || '').localeCompare(a.date || '')));
        } catch (error) {
            console.error('Failed to fetch items:', error);
            toast.error('Failed to load items');
        } finally {
            setLoading(false);
        }
    };

    const fetchStudentsAndAttendance = async () => {
        setLoading(true);
        setSelectedGroupFilter('all');
        try {
            const item = items.find(i => i.id === selectedItem);
            if (!item) return;
            setSelectedItemType(item._type);

            let studentsRes, attendanceRes, groupsRes;
            if (item._type === 'lesson') {
                [studentsRes, attendanceRes, groupsRes] = await Promise.all([
                    axios.get(`${API}/lessons/${selectedItem}/enrolled-students`, { headers: getAuthHeaders() }),
                    axios.get(`${API}/attendance/${selectedItem}`, { headers: getAuthHeaders() }),
                    axios.get(`${API}/lessons/${selectedItem}/groups`, { headers: getAuthHeaders() })
                ]);
            } else if (item._type === 'assessment') {
                [studentsRes, attendanceRes, groupsRes] = await Promise.all([
                    axios.get(`${API}/assessments/${selectedItem}/enrolled-students`, { headers: getAuthHeaders() }),
                    axios.get(`${API}/attendance/assessment/${selectedItem}`, { headers: getAuthHeaders() }),
                    axios.get(`${API}/assessments/${selectedItem}/groups`, { headers: getAuthHeaders() })
                ]);
            } else {
                // skill_assessment
                const [saRes, attRes] = await Promise.all([
                    axios.get(`${API}/skill-assessments/${selectedItem}`, { headers: getAuthHeaders() }),
                    axios.get(`${API}/skill-assessments/${selectedItem}/attendance`, { headers: getAuthHeaders() })
                ]);
                const allStudentsRes = await axios.get(`${API}/students`, { headers: getAuthHeaders() });
                const enrolled = allStudentsRes.data.filter(s => saRes.data.enrolled_student_ids?.includes(s.id));
                studentsRes = { data: enrolled };
                attendanceRes = { data: attRes.data };
                groupsRes = { data: saRes.data.local_groups || [] };
            }
            setStudents(studentsRes.data);
            setGroups(groupsRes.data);
            const attendanceMap = {};
            studentsRes.data.forEach(student => { attendanceMap[student.id] = false; });
            attendanceRes.data.forEach(record => { attendanceMap[record.student_id] = record.present; });
            setAttendance(attendanceMap);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Failed to load attendance data');
        } finally {
            setLoading(false);
        }
    };

    const markAll = (present) => {
        const newAttendance = {};
        students.forEach(s => { newAttendance[s.id] = present; });
        setAttendance(newAttendance);
    };

    const handleSave = async () => {
        if (!selectedItem) return;
        setSaving(true);
        try {
            const records = students.map(s => ({ student_id: s.id, present: attendance[s.id] || false }));
            if (selectedItemType === 'skill_assessment') {
                await axios.post(`${API}/skill-assessments/${selectedItem}/attendance`, { records }, { headers: getAuthHeaders() });
            } else {
                const payload = selectedItemType === 'lesson'
                    ? { lesson_id: selectedItem, records }
                    : { assessment_id: selectedItem, records };
                await axios.post(`${API}/attendance`, payload, { headers: getAuthHeaders() });
            }
            toast.success('Attendance saved successfully');
        } catch (error) {
            toast.error('Failed to save attendance');
        } finally {
            setSaving(false);
        }
    };

    const filteredItems = items.filter(i => {
        const matchesYear = selectedYear === 'all' || i.year === Number(selectedYear);
        const matchesSearch = !itemSearch || i.title.toLowerCase().includes(itemSearch.toLowerCase());
        return matchesYear && matchesSearch;
    });

    const selectedItemData = items.find(i => i.id === selectedItem);
    const presentCount = Object.values(attendance).filter(Boolean).length;
    const absentCount = students.length - presentCount;

    const sortBySurname = (list) => [...list].sort((a, b) => {
        if ((a.year || 0) !== (b.year || 0)) return (a.year || 0) - (b.year || 0);
        const surnameA = (a.name || '').split(' ').pop().toLowerCase();
        const surnameB = (b.name || '').split(' ').pop().toLowerCase();
        return surnameA.localeCompare(surnameB);
    });

    const filteredStudents = (() => {
        let list = selectedGroupFilter === 'all'
            ? students
            : students.filter(s => groups.find(g => g.id === selectedGroupFilter)?.student_ids?.includes(s.id));
        if (searchQuery) {
            list = list.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()) || s.student_id.toLowerCase().includes(searchQuery.toLowerCase()));
        }
        return sortBySurname(list);
    })();

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="attendance-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl font-bold text-ica-navy">Attendance</h1>
                        <p className="text-gray-500 mt-1">Mark student attendance for lessons and assessments</p>
                    </div>
                </div>

                {/* Item Selector with Year Filter & Search */}
                <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1 max-w-sm">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <Input value={itemSearch} onChange={e => setItemSearch(e.target.value)} placeholder="Search items..." className="pl-10" data-testid="attendance-item-search" />
                        </div>
                        <div className="flex gap-2">
                            {['all', '1', '2', '3'].map(y => (
                                <button key={y} onClick={() => setSelectedYear(y)}
                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedYear === y ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                    data-testid={`att-year-filter-${y}`}>
                                    {y === 'all' ? 'All' : `Year ${y}`}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Select Lesson, Assessment, or Skill Assessment</label>
                        <Select value={selectedItem || undefined} onValueChange={setSelectedItem}>
                            <SelectTrigger className="max-w-md" data-testid="attendance-item-selector">
                                <SelectValue placeholder="Choose an item..." />
                            </SelectTrigger>
                            <SelectContent>
                                {filteredItems.map(item => (
                                    <SelectItem key={item.id} value={item.id}>
                                        <span className="flex items-center gap-2">
                                            {item._type === 'lesson' ? <ChefHat className="w-3.5 h-3.5 text-ica-navy inline" /> :
                                             item._type === 'skill_assessment' ? <Zap className="w-3.5 h-3.5 text-amber-500 inline" /> :
                                             <FileText className="w-3.5 h-3.5 text-ica-gold inline" />}
                                            {item.title} (Year {item.year}) — {item.date}
                                        </span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {selectedItem && (
                    <>
                        {/* Stats + Search + Actions */}
                        <div className="flex flex-wrap items-center gap-4">
                            <div className="bg-white rounded-lg border border-gray-200 px-6 py-4 flex items-center gap-3">
                                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center"><Check className="w-5 h-5 text-green-600" /></div>
                                <div><p className="text-2xl font-bold text-gray-900">{presentCount}</p><p className="text-sm text-gray-500">Present</p></div>
                            </div>
                            <div className="bg-white rounded-lg border border-gray-200 px-6 py-4 flex items-center gap-3">
                                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center"><X className="w-5 h-5 text-red-600" /></div>
                                <div><p className="text-2xl font-bold text-gray-900">{absentCount}</p><p className="text-sm text-gray-500">Absent</p></div>
                            </div>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <Input
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    placeholder="Search students..."
                                    className="pl-9 w-[200px] h-10"
                                    data-testid="attendance-search-input"
                                />
                            </div>
                            <div className="flex-1" />
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => markAll(true)} data-testid="mark-all-present">Mark All Present</Button>
                                <Button variant="outline" size="sm" onClick={() => markAll(false)} data-testid="mark-all-absent">Mark All Absent</Button>
                                <Button onClick={handleSave} disabled={saving} data-testid="save-attendance-btn" className="bg-ica-gold hover:bg-ica-gold-dark">
                                    {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Attendance</>}
                                </Button>
                            </div>
                        </div>

                        {/* Group Filter */}
                        {groups.length > 0 && (
                            <div className="flex gap-1.5 flex-wrap">
                                <button onClick={() => setSelectedGroupFilter('all')}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedGroupFilter === 'all' ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                    data-testid="att-group-filter-all">
                                    All ({students.length})
                                </button>
                                {groups.map(g => (
                                    <button key={g.id} onClick={() => setSelectedGroupFilter(g.id)}
                                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedGroupFilter === g.id ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                        data-testid={`att-group-filter-${g.id}`}>
                                        {g.name} ({(g.student_ids || []).filter(sid => students.some(s => s.id === sid)).length})
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Students List */}
                        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                            {loading ? (
                                <div className="p-8 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy mx-auto" /></div>
                            ) : filteredStudents.length > 0 ? (
                                <div className="divide-y divide-gray-100">
                                    {filteredStudents.map((student) => {
                                        const isPresent = attendance[student.id] || false;
                                        return (
                                            <div key={student.id} data-testid={`attendance-row-${student.id}`}
                                                className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 bg-ica-navy/10 rounded-full flex items-center justify-center"><GraduationCap className="w-5 h-5 text-ica-navy" /></div>
                                                    <div>
                                                        <p className="font-medium text-gray-900">{student.name}</p>
                                                        <p className="text-sm text-gray-500">{student.student_id}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button onClick={() => setAttendance(prev => ({ ...prev, [student.id]: true }))} data-testid={`present-${student.id}`}
                                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${isPresent ? 'bg-green-500 text-white shadow-md' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}>
                                                        <Check className="w-4 h-4" /> Present
                                                    </button>
                                                    <button onClick={() => setAttendance(prev => ({ ...prev, [student.id]: false }))} data-testid={`absent-${student.id}`}
                                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${!isPresent ? 'bg-red-500 text-white shadow-md' : 'bg-red-50 text-red-700 hover:bg-red-100'}`}>
                                                        <X className="w-4 h-4" /> Absent
                                                    </button>
                                                    <button
                                                        onClick={() => navigate(selectedItemType === 'lesson' ? `/lessons/${selectedItem}` : `/assessments/${selectedItem}`)}
                                                        className="p-2 rounded-lg text-slate-500 hover:text-ica-navy hover:bg-gray-100 dark:text-slate-400 dark:hover:text-ica-gold dark:hover:bg-slate-700 active:scale-90 transition-all"
                                                        title="View Detail" data-testid={`drill-down-${student.id}`}>
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-10">
                                    <GraduationCap className="w-10 h-10 mx-auto mb-3 text-gray-400 opacity-40" />
                                    <h3 className="text-base font-semibold text-gray-700 mb-1">{searchQuery ? 'No matching students' : 'No students enrolled'}</h3>
                                    <p className="text-gray-500">{searchQuery ? 'Try a different search term' : `No students are enrolled in this ${selectedItemType}`}</p>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {!selectedItem && !loading && (
                    <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                        <ClipboardCheck className="w-10 h-10 mx-auto mb-3 text-gray-400 opacity-40" />
                        <h3 className="text-base font-semibold text-gray-700 mb-1">Select a lesson or assessment</h3>
                        <p className="text-gray-500">Choose an item above to mark attendance</p>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default AttendancePage;
